/**
 * Signup Modal
 * Handles new user registration flow
 */

import { ToasterService } from '@/services/ToasterService';
import { ModalSystem } from '@/services/ModalSystem';
import { loadOEMTemplate } from '@/services/OEMUtils';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('Signup');

/**
 * Show the signup/request access modal
 */
export async function showSignupModal(): Promise<void> {
  try {
    const html = await loadOEMTemplate('/forms/auth/signup.html');

    ModalSystem.show({
      title: 'Request Access',
      html,
      width: '500px',
      onOpened: () => {
        setupSignupForm();
      }
    });
  } catch (error) {
    logger.error('Error loading signup form:', error);
    ToasterService.error('Failed to load signup form');
  }
}

/**
 * Setup the signup form submission handler
 */
function setupSignupForm(): void {
  const form = document.getElementById('signup-form') as HTMLFormElement;
  if (!form) {
    logger.warn('Signup form not found');
    return;
  }

  // Handle "Sign in" link - just close the modal
  const signInLink = document.getElementById('signup-signin-link');
  logger.debug('Sign in link found:', !!signInLink);
  if (signInLink) {
    signInLink.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      logger.debug('Sign in link clicked - closing modal');
      ModalSystem.hide();
    });
  } else {
    logger.warn('Signup sign-in link not found');
  }

  // Handle Terms and Privacy links - open in new tab without status bar display
  const termsLink = document.getElementById('terms-link');
  const privacyLink = document.getElementById('privacy-link');
  
  [termsLink, privacyLink].forEach(link => {
    if (link) {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const url = link.getAttribute('data-url');
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      });
    }
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const formData = new FormData(form);
    
    const fullName = formData.get('fullName') as string;
    const email = formData.get('email') as string;
    const company = formData.get('company') as string;
    const reason = formData.get('reason') as string;

    // Validate required fields
    if (!fullName || !email) {
      ToasterService.warning('Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      ToasterService.warning('Please enter a valid email address');
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Submitting...';

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      ToasterService.success('Access request submitted! You will receive an email shortly.');
      ModalSystem.hide();
    } catch (error) {
      logger.error('Failed to submit access request:', error);
      ToasterService.error('Failed to submit request. Please try again.');
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  });
}
